import { useRef, useState } from "react";
import * as clientsApi from "../../api/clients";
import { getErrorMessage } from "../../api/client";
import { Modal } from "./Modal";
import { Button } from "./Button";

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Cliente già scelto (si arriva dalla sua pagina). Se manca, il cliente si
   * cerca dalla Partita IVA scritta nella Scheda.
   */
  clienteId?: string;
  /** Chiamato dopo che la Scheda è stata applicata davvero. */
  onApplicata?: (esito: clientsApi.EsitoScheda) => void;
}

/**
 * Carica una Scheda Cliente in due tempi: prima si vede che cosa cambierebbe,
 * poi si conferma.
 *
 * ## Perché due tempi
 *
 * Prima il caricamento scriveva subito e mostrava il resoconto dopo: se la
 * Scheda era quella sbagliata, o l'aveva compilata qualcuno con dati vecchi, lo
 * si scopriva a cose fatte. Le scritture sono solo su campi vuoti, quindi non
 * si perde niente — ma riempire venti caselle del cliente sbagliato è comunque
 * una mezz'ora di lavoro a disfarle a mano.
 *
 * ## Il file si rimanda al momento della conferma
 *
 * L'anteprima non lascia niente sul server (vedi il commento in
 * `routes/schedaCliente.ts`): il `File` resta qui nel browser e riparte con la
 * conferma. È la stessa forma di ImportModal.
 */
export function SchedaClienteAnteprima({ open, onClose, clienteId, onApplicata }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [anteprima, setAnteprima] = useState<clientsApi.EsitoAnteprimaScheda | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [caricando, setCaricando] = useState(false);
  const [applicando, setApplicando] = useState(false);
  /**
   * Quale sede si è scelta per ciascun foglio ambiguo: `{foglio: sede_id}`.
   *
   * Scegliendo si RICHIAMA l'anteprima invece di tenere la scelta solo qui: i
   * numeri di quel punto vendita (quanti campi si riempiono, quanti restano)
   * li sa solo il backend, e mostrarli calcolati altrove vorrebbe dire una
   * seconda implementazione della stessa regola.
   */
  const [scelteSedi, setScelteSedi] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);

  function ripulisci() {
    setFile(null);
    setAnteprima(null);
    setErrore(null);
    setScelteSedi({});
    if (inputRef.current) inputRef.current.value = "";
  }

  function chiudi() {
    ripulisci();
    onClose();
  }

  async function scegliFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setAnteprima(null);
    setErrore(null);
    setScelteSedi({});
    setCaricando(true);
    try {
      setAnteprima(await clientsApi.anteprimaSchedaCliente(f, clienteId));
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile leggere la Scheda."));
    } finally {
      setCaricando(false);
    }
  }

  /**
   * Rilegge la stessa Scheda dicendo di quale cliente si tratta.
   *
   * Si arriva qui solo dalla scelta fra più clienti con la stessa Partita IVA:
   * il file è già nel browser, quindi non si ricarica niente — si rifà la
   * stessa domanda con un'informazione in più.
   */
  async function scegliCandidato(clienteScelto: clientsApi.ClienteCandidatoScheda) {
    if (!file) return;
    setCaricando(true);
    setErrore(null);
    try {
      setAnteprima(await clientsApi.anteprimaSchedaCliente(file, clienteScelto.id));
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile leggere la Scheda."));
    } finally {
      setCaricando(false);
    }
  }

  async function scegliSede(foglio: string, sedeId: string) {
    if (!file) return;
    const aggiornate = { ...scelteSedi, [foglio]: sedeId };
    setScelteSedi(aggiornate);
    setCaricando(true);
    setErrore(null);
    try {
      setAnteprima(
        await clientsApi.anteprimaSchedaCliente(file, anteprima?.cliente?.id ?? clienteId, aggiornate)
      );
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile rileggere la Scheda."));
    } finally {
      setCaricando(false);
    }
  }

  async function conferma() {
    if (!file || !anteprima?.cliente) return;
    setApplicando(true);
    try {
      const { scheda } = await clientsApi.uploadDocumentoCliente(
        anteprima.cliente.id,
        file,
        "scheda_cliente_compilata",
        undefined,
        scelteSedi
      );
      if (scheda) onApplicata?.(scheda);
      chiudi();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile applicare la Scheda."));
    } finally {
      setApplicando(false);
    }
  }

  const esito = anteprima?.esito;
  const sediAgganciate = esito?.sedi.filter((s) => s.sede_id) ?? [];
  const sediDaScegliere = esito?.sedi.filter((s) => !s.sede_id && s.candidate) ?? [];
  const sediNonTrovate = esito?.sedi.filter((s) => !s.sede_id && !s.candidate) ?? [];
  const nienteDaFare =
    !!esito && esito.campi_cliente.length === 0 && sediAgganciate.every((s) => s.campi_riempiti.length === 0);
  // Applicare con una scelta ancora aperta scriverebbe tutto il resto e
  // lascerebbe indietro quel punto vendita in silenzio: meglio fermarsi qui,
  // dove la scelta è a un click.
  const scelteAperte = sediDaScegliere.length > 0;

  return (
    <Modal
      open={open}
      onClose={chiudi}
      title="Carica una Scheda Cliente"
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={chiudi}>
            Annulla
          </Button>
          <Button
            type="button"
            onClick={conferma}
            loading={applicando}
            disabled={!anteprima?.cliente || !!anteprima?.errore || nienteDaFare || scelteAperte}
          >
            Applica al cliente
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-ink">File della Scheda (.xlsx)</label>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={scegliFile}
            className="text-sm text-ink file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-1.5 file:text-sm file:text-primary"
          />
          {!clienteId && (
            <p className="text-xs text-ink/50">
              Il cliente si riconosce dalla Partita IVA scritta nella Scheda. I clienti arrivano da Odoo:
              da qui non se ne creano di nuovi.
            </p>
          )}
        </div>

        {caricando && <p className="text-sm text-ink/60">Lettura della Scheda…</p>}
        {errore && <p className="rounded-lg bg-accent/10 px-3 py-2 text-sm text-accent">{errore}</p>}

        {anteprima?.errore && (
          <div className="rounded-lg border border-accent/30 bg-accent/5 px-3 py-2.5 text-sm text-ink">
            {anteprima.errore}
          </div>
        )}

        {anteprima?.candidati && anteprima.candidati.length > 0 && (
          <div className="flex flex-col gap-2">
            {/* Non è un errore, e il testo lo dice: capita che un gruppo e il
                suo cliente «storico» condividano la partita IVA. Chiamarla
                anomalia manderebbe qualcuno a "sistemare" due anagrafiche che
                sono giuste così. */}
            <p className="text-sm text-ink">
              La Partita IVA <strong>{anteprima.partita_iva_letta}</strong> è di{" "}
              {anteprima.candidati.length} clienti. Succede, e non è un errore da correggere: scegli
              quale aggiornare con questa Scheda.
            </p>
            <ul className="flex flex-col gap-2">
              {anteprima.candidati.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => scegliCandidato(c)}
                    className="flex w-full flex-col items-start gap-0.5 rounded-lg border border-line px-3 py-2.5 text-left hover:border-primary/40 hover:bg-primary/5"
                  >
                    <span className="text-sm font-semibold text-ink">
                      {c.nome}
                      {c.insegna && <span className="font-normal text-ink/50"> · {c.insegna}</span>}
                    </span>
                    <span className="text-xs text-ink/50">
                      {c.numero_sedi === 0
                        ? "nessuna sede collegata"
                        : `${c.numero_sedi} sed${c.numero_sedi === 1 ? "e" : "i"}: ${c.sedi
                            .map((s) => [s.nome_sede, s.citta].filter(Boolean).join(" — "))
                            .join(" · ")}${c.numero_sedi > c.sedi.length ? " …" : ""}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {anteprima?.cliente && esito && (
          <>
            <div className="rounded-lg border border-line bg-ink/[0.02] px-3 py-2.5 text-sm">
              <span className="text-ink/50">Cliente riconosciuto: </span>
              <span className="font-semibold text-ink">{anteprima.cliente.nome}</span>
              {anteprima.cliente.insegna && <span className="text-ink/50"> · {anteprima.cliente.insegna}</span>}
              {anteprima.partita_iva_letta && (
                <span className="text-ink/50"> — P.IVA {anteprima.partita_iva_letta}</span>
              )}
            </div>

            {/* Niente si scrive finché non si conferma: detto qui e non solo
                nel bottone, perché è la ragione per cui questa schermata esiste. */}
            <p className="text-xs text-ink/50">
              Niente è ancora stato scritto. Confermando, si riempiono <strong>solo i campi vuoti</strong>:
              quello che è già a sistema non viene mai sostituito.
            </p>

            <Sezione titolo={`Da riempire sul cliente (${esito.campi_cliente.length})`}>
              {esito.campi_cliente.length === 0 ? (
                <p className="text-sm text-ink/50">Nessuno: l'anagrafica è già completa per i campi della Scheda.</p>
              ) : (
                <p className="text-sm text-ink">{esito.campi_cliente.join(", ")}</p>
              )}
            </Sezione>

            <Sezione titolo={`Già presenti, non verranno toccati (${esito.campi_saltati.length})`}>
              {esito.campi_saltati.length === 0 ? (
                <p className="text-sm text-ink/50">Nessuna divergenza fra la Scheda e quello che c'è a sistema.</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {esito.campi_saltati.map((c) => (
                    <li key={c.campo} className="text-ink/70">
                      <span className="font-medium text-ink">{c.campo}</span>: resta «{c.attuale}», la Scheda
                      dice «{c.nel_modulo}»
                    </li>
                  ))}
                </ul>
              )}
            </Sezione>

            <Sezione titolo={`Punti vendita (${esito.sedi.length})`}>
              {esito.sedi.length === 0 ? (
                <p className="text-sm text-ink/50">La Scheda non contiene fogli di punto vendita.</p>
              ) : (
                <ul className="flex flex-col gap-1 text-sm">
                  {sediAgganciate.map((s) => (
                    <li key={s.foglio} className="text-ink">
                      «{s.foglio}» → sede collegata, {s.campi_riempiti.length} camp
                      {s.campi_riempiti.length === 1 ? "o" : "i"} da riempire
                      {s.campi_saltati.length > 0 && (
                        <span className="text-ink/50">
                          , {s.campi_saltati.length} già present{s.campi_saltati.length === 1 ? "e" : "i"}
                        </span>
                      )}
                    </li>
                  ))}
                  {sediDaScegliere.map((s) => (
                    <li key={s.foglio} className="flex flex-col gap-1.5 rounded-lg border border-[#f97316]/30 bg-[#f97316]/5 p-2.5">
                      <span className="text-ink">
                        «{s.foglio}» → {s.candidate!.length} sedi possibili. Scegli quale aggiornare: finché
                        non scegli, questo punto vendita non viene toccato.
                      </span>
                      <div className="flex flex-col gap-1">
                        {s.candidate!.map((c) => (
                          <button
                            key={c.sede_id}
                            type="button"
                            onClick={() => scegliSede(s.foglio, c.sede_id)}
                            className="rounded-lg border border-line bg-white px-2.5 py-1.5 text-left text-sm hover:border-primary/40 hover:bg-primary/5"
                          >
                            <span className="font-medium text-ink">{c.nome_sede ?? "Sede senza nome"}</span>
                            <span className="text-ink/50">
                              {[c.indirizzo, c.citta].filter(Boolean).length > 0 &&
                                ` — ${[c.indirizzo, c.citta].filter(Boolean).join(", ")}`}
                            </span>
                            {/* Da quale dei due criteri arriva: è l'informazione
                                che spiega perché la scelta è aperta. */}
                            <span className="block text-xs text-ink/40">trovata per {c.trovata_da}</span>
                          </button>
                        ))}
                      </div>
                    </li>
                  ))}
                  {sediNonTrovate.map((s) => (
                    <li key={s.foglio} className="text-accent">
                      «{s.foglio}» → nessuna sede corrisponde, né per insegna né per indirizzo: non verrà creata
                    </li>
                  ))}
                </ul>
              )}
            </Sezione>

            {esito.avvisi.length > 0 && (
              <Sezione titolo={`Da guardare (${esito.avvisi.length})`}>
                <ul className="flex list-disc flex-col gap-1 pl-4 text-sm text-ink/70">
                  {esito.avvisi.map((a, i) => (
                    <li key={i}>{a}</li>
                  ))}
                </ul>
              </Sezione>
            )}

            {nienteDaFare && (
              <p className="rounded-lg bg-ink/[0.03] px-3 py-2 text-sm text-ink/60">
                Non c'è niente da scrivere: la Scheda non aggiunge nulla che non ci sia già.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

function Sezione({ titolo, children }: { titolo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink/40">{titolo}</p>
      {children}
    </div>
  );
}
